import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import useRequests from "./http";
import { useAuth } from "./useAuth";

interface TenantContextType {
  tenants: Tenant[];
  activeTenant: Tenant | null;
  setActiveTenant: (tenant: Tenant) => Promise<void>;
  addTenant: (tenant: Tenant) => void;
  updateTenant: (tenant: Tenant) => void;
  removeTenant: (tenantId: number) => void;
  loading: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

const STORAGE_KEY = "janus_active_tenant_id";

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
}

export const TenantProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, refresh } = useAuth();
  const { get, post } = useRequests();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [activeTenant, setActiveTenantState] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);

  const setActiveTenant = useCallback(
    async (tenant: Tenant) => {
      const { status } = await post("/platform/self/switch-tenant", null, { tenant_id: tenant.id });
      if (status !== 200) return;
      setActiveTenantState(tenant);
      localStorage.setItem(STORAGE_KEY, String(tenant.id));
      await refresh();
    },
    [post, refresh],
  );

  const addTenant = useCallback((tenant: Tenant) => {
    setTenants((prev) => [...prev, tenant]);
    setActiveTenantState(tenant);
    localStorage.setItem(STORAGE_KEY, String(tenant.id));
  }, []);

  const updateTenant = useCallback((tenant: Tenant) => {
    setTenants((prev) => prev.map((t) => (t.id === tenant.id ? tenant : t)));
    setActiveTenantState((prev) => (prev?.id === tenant.id ? tenant : prev));
  }, []);

  const removeTenant = useCallback((tenantId: number) => {
    setTenants((prev) => prev.filter((t) => t.id !== tenantId));
    setActiveTenantState((prev) => {
      if (prev?.id !== tenantId) return prev;
      return null;
    });
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && Number(saved) === tenantId) {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setTenants([]);
      setActiveTenantState(null);
      setLoading(false);
      return;
    }

    get("/platform/self/memberships")
      .then(({ data, status }) => {
        if (status === 200 && Array.isArray(data)) {
          setTenants(data);

          const savedId = localStorage.getItem(STORAGE_KEY);
          const saved = savedId
            ? data.find((t: Tenant) => t.id === Number(savedId))
            : null;
          setActiveTenantState(saved ?? data[0] ?? null);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [user, get]);

  const value = useMemo(
    () => ({ tenants, activeTenant, setActiveTenant, addTenant, updateTenant, removeTenant, loading }),
    [tenants, activeTenant, setActiveTenant, addTenant, updateTenant, removeTenant, loading]
  );

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
};
